/* eslint-disable react/no-array-index-key */
/* eslint-disable react/require-default-props */
import React, { useState } from 'react';
import moment from 'moment';
import { saveAs } from 'file-saver';
import BiddingInfoModal from 'components/molecules/bidding_info_modal';
import Loader from 'components/atoms/loader';
import iconRobotIa from 'assets/images/icon_robot_ia.svg';
import { useHiringHistoryOcorrencias } from 'hooks/useHiringHistoryOcorrencias';
import {
  OcorrenciaResponse,
  OcorrenciaTipo,
  downloadOcorrenciaZip,
} from 'services/actions/ai_management_hiring_history';
import { downloadPossibleCompetitorOcorrenciaZip } from 'services/actions/ai_management_possible_competitors';
import { BiddingInfoDetailTypeSubtype, BiddingInfoTypes } from 'store/modules/bidding_store/types';
import { Content, ModalLayer } from './styles';

interface AiManagementOcorrenciasTableProps {
  biddingId: number | null;
  historicBiddingId: number;
  wrap: (content: React.ReactNode) => React.ReactElement;
  cnpj?: string;
  alwaysShow?: boolean;
  emptyText?: string;
  theadClassName?: string;
}

const RESUMO_LIMIT = 220;

const TIPO_LABEL: Record<OcorrenciaTipo, string> = {
  esclarecimento: 'Esclarecimento',
  impugnacao: 'Impugnação',
  aviso: 'Aviso',
  recurso: 'Recurso',
  diligencia: 'Diligência',
};

const OCORRENCIA_TYPE_ID: Record<OcorrenciaTipo, number> = {
  esclarecimento: 1,
  impugnacao: 2,
  aviso: 3,
  recurso: 4,
  diligencia: 5,
};

const isoDate = (value: string | null | undefined) => {
  if (!value) return '';
  const parsed = moment(value, ['DD/MM/YYYY', moment.ISO_8601], true);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
};

const fileName = (url: string) => {
  try {
    return decodeURIComponent(new URL(url).pathname.split('/').pop() || '') || 'arquivo';
  } catch {
    return 'arquivo';
  }
};

const toBiddingInfo = (
  ocorrencia: OcorrenciaResponse,
  historicBiddingId: number,
): BiddingInfoTypes => {
  const { pergunta, resposta } = ocorrencia.dialogo ?? {};
  const typeId = OCORRENCIA_TYPE_ID[ocorrencia.tipo];
  const lado = (
    id: number,
    nome: string,
    dados: { descricao: string | null; data: string | null } | undefined,
    urls: string[],
  ) =>
    ({
      id,
      description: dados?.descricao ?? '',
      message_date: isoDate(dados?.data),
      bidding_id: historicBiddingId,
      type_id: typeId,
      subtype: { subtype_name: nome },
      urls: urls.map((url, index) => ({ id: index, url, filename: fileName(url) })),
    } as unknown as BiddingInfoDetailTypeSubtype);

  return {
    id: historicBiddingId,
    title: pergunta?.descricao ?? ocorrencia.resumo ?? '',
    situation: resposta?.descricao ? 'Respondido' : 'Sem resposta',
    message_date: isoDate(pergunta?.data),
    bidding_id: historicBiddingId,
    type_id: typeId,
    detail_type_subtypes: [
      lado(1, 'Pergunta', pergunta, ocorrencia.urls),
      ...(resposta?.descricao ? [lado(2, 'Resposta', resposta, [])] : []),
    ],
  } as unknown as BiddingInfoTypes;
};

const hasDialogo = ({ dialogo }: OcorrenciaResponse) =>
  Boolean(dialogo?.pergunta?.descricao || dialogo?.resposta?.descricao);

const hasConteudo = (ocorrencia: OcorrenciaResponse) =>
  Boolean(ocorrencia.resumo || ocorrencia.urls.length || hasDialogo(ocorrencia));

const OcorrenciaRow: React.FC<{
  ocorrencia: OcorrenciaResponse;
  indice: number;
  biddingId: number | null;
  historicBiddingId: number;
  cnpj?: string;
}> = ({ ocorrencia, indice, biddingId, historicBiddingId, cnpj }) => {
  const [detalhesAberto, setDetalhesAberto] = useState(false);
  const [expandido, setExpandido] = useState(false);
  const [baixando, setBaixando] = useState(false);
  const { dialogo, resumo, urls } = ocorrencia;
  const pergunta = dialogo?.pergunta?.descricao?.trim();
  const sintetizado =
    ocorrencia.tipo === 'impugnacao' && Boolean(resumo) && resumo?.trim() !== pergunta;
  const showDetails = hasDialogo(ocorrencia);
  const resumoLongo = (resumo?.length ?? 0) > RESUMO_LIMIT;
  const resumoVisivel =
    resumo && resumoLongo && !expandido ? `${resumo.slice(0, RESUMO_LIMIT).trimEnd()}...` : resumo;

  const baixarZip = async () => {
    if (!biddingId || baixando) return;
    setBaixando(true);
    try {
      const { data } = cnpj
        ? await downloadPossibleCompetitorOcorrenciaZip(biddingId, historicBiddingId, cnpj, indice)
        : await downloadOcorrenciaZip(biddingId, historicBiddingId, indice);
      saveAs(data, `ocorrencia-${ocorrencia.tipo}-${historicBiddingId}.zip`);
    } catch {
      window.alert('Não foi possível baixar os arquivos. Tente novamente.');
    } finally {
      setBaixando(false);
    }
  };

  return (
    <tr>
      <td className="text-nowrap align-middle">
        <span className="badge ocorrencia-badge">{TIPO_LABEL[ocorrencia.tipo]}</span>
      </td>
      <td className="align-middle">
        {sintetizado ? (
          <div className="ocorrencia-ia mb-1">
            <img src={iconRobotIa} alt="" aria-hidden />
            Sintetizado por IA
          </div>
        ) : null}
        {resumo ? (
          <div className="ocorrencia-resumo">
            {resumoVisivel}
            {resumoLongo ? (
              <button
                type="button"
                className="btn btn-link p-0 ml-1 align-baseline"
                onClick={() => setExpandido((current) => !current)}
                aria-expanded={expandido}
              >
                {expandido ? '[-]' : '[+]'}
              </button>
            ) : null}
          </div>
        ) : null}
        {ocorrencia.empresa ? (
          <div className="small text-muted mt-1">{ocorrencia.empresa}</div>
        ) : null}
      </td>
      <td className="text-nowrap align-middle text-right">
        {urls.length === 1 ? (
          <a href={urls[0]} target="_blank" rel="noopener noreferrer">
            Download
          </a>
        ) : null}
        {urls.length > 1 ? (
          <button
            type="button"
            className="btn btn-link p-0"
            onClick={baixarZip}
            disabled={baixando}
          >
            {baixando ? 'Baixando...' : 'Download'}
          </button>
        ) : null}
        {showDetails ? (
          <div>
            <button
              type="button"
              className="btn btn-link btn-sm p-0"
              onClick={() => setDetalhesAberto(true)}
            >
              Detalhes
            </button>
          </div>
        ) : null}
      </td>

      {detalhesAberto ? (
        <BiddingInfoModal
          showModal
          setShowModal={setDetalhesAberto}
          typeName="Detalhes"
          bidding_info={[toBiddingInfo(ocorrencia, historicBiddingId)]}
          className="hiring-history-info-modal"
          backdropClassName="hiring-history-info-backdrop"
          hideBiddingNumber
          initiallyOpen
          trackEvents={false}
        />
      ) : null}
    </tr>
  );
};

const AiManagementOcorrenciasTable: React.FC<AiManagementOcorrenciasTableProps> = ({
  biddingId,
  historicBiddingId,
  wrap,
  cnpj,
  alwaysShow = false,
  emptyText = 'Nenhuma ocorrência registrada para esta licitação.',
  theadClassName = '',
}) => {
  const { ocorrencias, pending, loading, error } = useHiringHistoryOcorrencias(
    biddingId,
    historicBiddingId,
    cnpj,
  );
  const message = (text: string) => wrap(<div className="p-3 text-muted">{text}</div>);

  if (loading) {
    const loader = (
      <div className="py-3">
        <Loader />
      </div>
    );
    return alwaysShow ? wrap(loader) : loader;
  }

  if (error) {
    return alwaysShow ? message('Não foi possível carregar as ocorrências desta licitação.') : null;
  }

  if (pending) {
    return message(
      'As ocorrências desta licitação estão sendo geradas. Abra novamente em instantes.',
    );
  }

  const visiveis = ocorrencias
    .map((ocorrencia, indice) => ({ ocorrencia, indice }))
    .filter(({ ocorrencia }) => hasConteudo(ocorrencia));

  if (!visiveis.length) {
    return alwaysShow ? message(emptyText) : null;
  }

  return (
    <>
      <ModalLayer />
      {wrap(
        <Content className="table-responsive">
          <table className="table bg-white mb-0">
            <thead className={theadClassName}>
              <tr>
                <th scope="col">Tipo</th>
                <th scope="col">Situação</th>
                <th scope="col" className="text-right">
                  Arquivos
                </th>
              </tr>
            </thead>
            <tbody>
              {visiveis.map(({ ocorrencia, indice }) => (
                <OcorrenciaRow
                  key={`${ocorrencia.tipo}-${indice}`}
                  ocorrencia={ocorrencia}
                  indice={indice}
                  biddingId={biddingId}
                  historicBiddingId={historicBiddingId}
                  cnpj={cnpj}
                />
              ))}
            </tbody>
          </table>
        </Content>,
      )}
    </>
  );
};

export default AiManagementOcorrenciasTable;
